import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout/Layout";
import "../styles/InvoiceStyles.css";

const Invoice = () => {
  const { accessToken } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const printRef = useRef();

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const { data } = await axios.get(`/api/v1/payments/invoice/${accessToken}`);
        if (data?.success) {
          setInvoice(data.invoice);
        } else {
          setError("الفاتورة غير موجودة");
        }
      } catch (err) {
        setError("حدث خطأ في تحميل الفاتورة");
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [accessToken]);

  const handlePrint = () => {
    window.print();
  };

  const getPaymentMethodLabel = (method) => {
    const map = {
      qi: "Qi Card - كي كارد",
      cash_on_delivery: "الدفع عند الاستلام",
      braintree: "بطاقة ائتمان",
    };
    return map[method] || method;
  };

  const getStatusLabel = (status) => {
    const map = {
      pending: "بانتظار الدفع",
      processing: "قيد المعالجة",
      completed: "مدفوع",
      failed: "فشل الدفع",
      refunded: "مسترجع",
      expired: "منتهي الصلاحية",
    };
    return map[status] || status;
  };

  const getStatusClass = (status) => {
    if (status === "completed") return "invoice-status-paid";
    if (status === "failed" || status === "expired") return "invoice-status-failed";
    return "invoice-status-pending";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("ar-IQ", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPrice = (price) => {
    return (price || 0).toLocaleString("ar-IQ") + " د.ع";
  };

  if (loading) {
    return (
      <Layout title="فاتورة - النخبة ستور">
        <div className="invoice-loading">
          <p>جاري تحميل الفاتورة...</p>
        </div>
      </Layout>
    );
  }

  if (error || !invoice) {
    return (
      <Layout title="فاتورة - النخبة ستور">
        <div className="invoice-loading">
          <p>{error || "الفاتورة غير موجودة"}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={`فاتورة ${invoice.invoiceNumber} - النخبة ستور`}>
      <div className="invoice-page">
        {/* Print button - hidden during print */}
        <div className="invoice-actions no-print">
          <button className="btn btn-gold-shimmer" onClick={handlePrint}>
            طباعة الفاتورة
          </button>
        </div>

        <div className="invoice-container" ref={printRef}>
          {/* Header */}
          <div className="invoice-header">
            <div className="invoice-store-info">
              <h1 className="invoice-store-name">{invoice.store.name}</h1>
              <p className="invoice-store-name-en">{invoice.store.nameEn}</p>
              <p>{invoice.store.website}</p>
              <p>{invoice.store.phone}</p>
            </div>
            <div className="invoice-meta">
              <h2 className="invoice-title">فاتورة</h2>
              <table className="invoice-meta-table">
                <tbody>
                  <tr>
                    <td>رقم الفاتورة:</td>
                    <td><strong>{invoice.invoiceNumber}</strong></td>
                  </tr>
                  <tr>
                    <td>رقم الطلب:</td>
                    <td>{invoice.orderId}</td>
                  </tr>
                  <tr>
                    <td>التاريخ:</td>
                    <td>{formatDate(invoice.date)}</td>
                  </tr>
                  <tr>
                    <td>حالة الدفع:</td>
                    <td>
                      <span className={`invoice-status ${getStatusClass(invoice.paymentStatus)}`}>
                        {getStatusLabel(invoice.paymentStatus)}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="invoice-divider"></div>

          {/* Customer Info */}
          <div className="invoice-customer">
            <h3>معلومات العميل</h3>
            <table className="invoice-customer-table">
              <tbody>
                <tr>
                  <td>الاسم:</td>
                  <td>{invoice.customer.name}</td>
                </tr>
                <tr>
                  <td>الهاتف:</td>
                  <td>{invoice.customer.phone}</td>
                </tr>
                <tr>
                  <td>المحافظة:</td>
                  <td>{invoice.customer.city}</td>
                </tr>
                <tr>
                  <td>العنوان:</td>
                  <td>{invoice.customer.address}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="invoice-divider"></div>

          {/* Items Table */}
          <div className="invoice-items">
            <table className="invoice-items-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>المنتج</th>
                  <th>الكمية</th>
                  <th>السعر</th>
                  <th>المجموع</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{item.name}</td>
                    <td>{item.quantity || 1}</td>
                    <td>{formatPrice(item.price)}</td>
                    <td>{formatPrice(item.price * (item.quantity || 1))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="invoice-totals">
            <div className="invoice-totals-row">
              <span>المجموع الفرعي:</span>
              <span>{formatPrice(invoice.subtotal)}</span>
            </div>
            {invoice.shippingFee > 0 && (
              <div className="invoice-totals-row">
                <span>رسوم التوصيل:</span>
                <span>{formatPrice(invoice.shippingFee)}</span>
              </div>
            )}
            <div className="invoice-totals-row invoice-grand-total">
              <span>المبلغ الإجمالي:</span>
              <span>{formatPrice(invoice.totalAmount)}</span>
            </div>
          </div>

          <div className="invoice-divider"></div>

          {/* Payment Info */}
          <div className="invoice-payment-info">
            <h3>معلومات الدفع</h3>
            <table className="invoice-customer-table">
              <tbody>
                <tr>
                  <td>طريقة الدفع:</td>
                  <td>{getPaymentMethodLabel(invoice.paymentMethod)}</td>
                </tr>
                {invoice.transactionId && (
                  <tr>
                    <td>رقم العملية:</td>
                    <td>{invoice.transactionId}</td>
                  </tr>
                )}
                {invoice.paidAt && (
                  <tr>
                    <td>تاريخ الدفع:</td>
                    <td>{formatDate(invoice.paidAt)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="invoice-notes">
              <strong>ملاحظات:</strong> {invoice.notes}
            </div>
          )}

          {/* Footer */}
          <div className="invoice-footer">
            <p>شكراً لتسوقكم من النخبة ستور</p>
            <p className="invoice-footer-small">هذه فاتورة إلكترونية صادرة من النظام</p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Invoice;
